import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiFetch } from '../lib/api'
import type { AppNotification } from '@fellowship/shared'

export interface NotificationsResult {
  notifications: AppNotification[]
  unreadCount: number
}

export function useNotifications() {
  return useQuery({
    queryKey: ['users', 'me', 'notifications'],
    queryFn: () => apiFetch<{ data: NotificationsResult }>('/users/me/notifications'),
    staleTime: 60 * 1000,
  })
}

export function useMarkAllRead() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => apiFetch('/users/me/notifications/read-all', { method: 'POST' }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['users', 'me', 'notifications'] })
    },
  })
}

export function useMarkNotificationRead() {
  const qc = useQueryClient()
  const key = ['users', 'me', 'notifications']
  return useMutation({
    mutationFn: (notificationId: string) =>
      apiFetch(`/users/me/notifications/${notificationId}/read`, { method: 'POST' }),
    // Clicking a notification navigates away and unmounts the row, so update the
    // cache optimistically — the read state must stick without waiting for the request.
    onMutate: (notificationId: string) => {
      const at = new Date().toISOString()
      qc.setQueryData<{ data: NotificationsResult }>(key, (prev) => {
        if (!prev) return prev
        let cleared = 0
        const notifications = prev.data.notifications.map((n) => {
          if (n.id === notificationId && !n.readAt) {
            cleared = 1
            return { ...n, readAt: at }
          }
          return n
        })
        return {
          data: { notifications, unreadCount: Math.max(0, prev.data.unreadCount - cleared) },
        }
      })
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: key })
    },
  })
}

export function useUpdateNotificationPreference() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (notifyNewEvents: boolean) =>
      apiFetch('/users/me', { method: 'PATCH', body: JSON.stringify({ notifyNewEvents }) }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['users', 'me'] })
    },
  })
}
